import * as React from 'react';
import {
  makeStyles,
  tokens,
  Card,
  CardPreview,
  Text,
  Button,
} from '@fluentui/react-components';
import { DiagnosticoEvento, ExtracaoDetalhe } from '../../../types/diagnostico';
import { ArrowRight24Regular, CheckmarkCircle32Filled } from '@fluentui/react-icons';
import { getSeverityText, countSeverities, getSeverityGradientColor } from '../../../utils/severityHelpers';

const useStyles = makeStyles({
  container: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingHorizontalM,
    justifyContent: 'flex-start',
    '@media (max-width: 1024px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    maxWidth: '100%',
    width: '100%',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  titleSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300,
  },
  date: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  scoreSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  scoreInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
  },
  score: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
  },
  scoreLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  progressBar: {
    width: '6px',
    height: '60px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusSmall,
    overflow: 'hidden',
    position: 'relative',
  },
  progressFill: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    transition: 'background-color 0.3s ease',
  },
  content: {
    padding: tokens.spacingHorizontalM,
  },
  recommendationsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  recommendationsText: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  severityDetails: {
    display: 'flex',
    flexDirection: 'row',
    gap: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase300,
    flexWrap: 'wrap',
  },
  severityItem: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  severityBar: {
    width: '100%',
    height: '6px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusSmall,
    overflow: 'hidden',
    position: 'relative',
  },
  severityFill: {
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    transition: 'width 0.3s ease',
  },
  actionButton: {
    alignSelf: 'flex-end',
    marginTop: tokens.spacingVerticalS,
  },
});

interface EventCardsProps {
  eventos: DiagnosticoEvento[];
  onEventClick: (evento: DiagnosticoEvento) => void;
  loading?: boolean;
}

const EventCards: React.FC<EventCardsProps> = ({
  eventos,
  onEventClick,
  loading = false,
}) => {
  const styles = useStyles();

  const getSeverityColor = (severity: number) => {
    return getSeverityGradientColor(severity as 0 | 1 | 2 | 3);
  };

  const getSeverityLabel = (severity: number) => {
    return getSeverityText(severity as 0 | 1 | 2 | 3);
  };

  const getAverageSeverity = (extracoes: ExtracaoDetalhe[]) => {
    const naoPontuadas = extracoes.filter(e => e.pontuacaoValidada === 0);
    if (naoPontuadas.length === 0) return 0;
    
    const totalSeverity = naoPontuadas.reduce((sum, e) => sum + e.estruturaEvento.severidade, 0);
    return totalSeverity / naoPontuadas.length;
  };

  const getSeverityCounts = (extracoes: ExtracaoDetalhe[]) => {
    const naoPontuadas = extracoes.filter(e => e.pontuacaoValidada === 0);
    const counts = countSeverities(naoPontuadas.map(e => e.estruturaEvento.severidade as 0 | 1 | 2 | 3));
    return {
      critico: counts[3], // severidade 3 = crítica
      alto: counts[2],    // severidade 2 = alta
      medio: counts[1],   // severidade 1 = média
      baixo: counts[0],   // severidade 0 = baixa
    };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getPercentualAtingimento = (evento: DiagnosticoEvento) => {
    if (evento.totalPontuacaoMaxima === 0) return 0;
    return (evento.totalPontuacaoValida / evento.totalPontuacaoMaxima) * 100;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        {[...Array(3)].map((_, index) => (
          <Card key={index} className={styles.card}>
            <CardPreview>
              <div className={styles.cardContent}>
                <div style={{ height: '120px', backgroundColor: tokens.colorNeutralBackground3 }} />
              </div>
            </CardPreview>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.container}>      
        {eventos.length === 0 ? (
        <Card className={styles.card} style={{ 
          gridColumn: '1 / -1',
          maxWidth: '600px',
          margin: '0 auto',
          cursor: 'default'
        }}>
          <div style={{ 
            textAlign: 'center', 
            padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXL}`,
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.spacingVerticalM,
            alignItems: 'center'
          }}>
            <Text style={{ 
              fontSize: tokens.fontSizeBase400, 
              fontWeight: tokens.fontWeightSemibold,
              color: tokens.colorNeutralForeground2
            }}>
              Nenhum diagnóstico disponível para este projeto
            </Text>
            <Text style={{ 
              fontSize: tokens.fontSizeBase300,
              color: tokens.colorNeutralForeground3
            }}>
              Clique em "Gerar novo diagnóstico" para iniciar a análise de uso da aplicação. Se os resultados não aparecerem, execute o assistente de atualização automática do ERP UAU para sincronizar os dados.
            </Text>
          </div>
        </Card>
      ) : (
        eventos.map((evento) => {
          const percentual = getPercentualAtingimento(evento);
          const naoPontuadas = evento.extracoes.filter(e => e.pontuacaoValidada === 0);
          const averageSeverity = getAverageSeverity(evento.extracoes);
          const severityCounts = getSeverityCounts(evento.extracoes);

          return (
            <Card
              key={evento.eventoId}
              className={styles.card}
              onClick={() => onEventClick(evento)}
            >
              <div className={styles.header}>
                <div className={styles.titleSection}>
                  <Text className={styles.title}>{evento.descricaoEvento}</Text>
                  <Text className={styles.date}>
                    {formatDate(evento.data)}
                  </Text>
                </div>
                <div className={styles.scoreSection}>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{
                        backgroundColor: percentual >= 80 
                          ? tokens.colorPaletteGreenForeground1 
                          : percentual >= 60 
                            ? tokens.colorPaletteYellowBackground3 
                            : tokens.colorPaletteRedForeground1,
                      }}
                    />
                  </div>
                  <div className={styles.scoreInfo}>
                    <Text className={styles.score}>{percentual.toFixed(2)}</Text>
                    <Text className={styles.scoreLabel}>Pontuação</Text>
                  </div>
                </div>
              </div>

              <div className={styles.content}>
                {percentual >= 100 ? (
                  <div style={{
                    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalM}`,
                    borderRadius: tokens.borderRadiusMedium,
                    marginBottom: tokens.spacingVerticalM,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: tokens.spacingHorizontalS,
                  }}>
                    <CheckmarkCircle32Filled
                      style={{
                        fontSize: '32px',
                        color: tokens.colorPaletteGreenForeground1,
                      }}
                    />
                    <Text style={{
                      fontSize: tokens.fontSizeBase300,
                      textAlign: 'center',
                    }}>
                      Parabéns! Você está seguindo todas as nossas recomendações de {evento.descricaoEvento}.
                    </Text>
                  </div>
                ) : (
                  <div className={styles.recommendationsSection}>
                    <div className={styles.recommendationsText}>
                      <Text>Recomendações ({naoPontuadas.length})</Text>
                      <Text>{getSeverityLabel(Math.round(averageSeverity))}</Text>
                    </div>
                    <div className={styles.severityBar}>
                      {naoPontuadas.length > 0 ? (
                        (() => {
                          const total = naoPontuadas.length;
                          const criticoPercent = (severityCounts.critico / total) * 100;
                          const altoPercent = (severityCounts.alto / total) * 100;
                          const medioPercent = (severityCounts.medio / total) * 100;
                          const baixoPercent = (severityCounts.baixo / total) * 100;

                          // Criar gradiente linear com as proporções
                          let gradientStops = [];
                          let currentPercent = 0;

                          if (severityCounts.critico > 0) {
                            gradientStops.push(`${getSeverityColor(3)} ${currentPercent}%`);
                            currentPercent += criticoPercent;
                            gradientStops.push(`${getSeverityColor(3)} ${currentPercent}%`);
                          }

                          if (severityCounts.alto > 0) {
                            gradientStops.push(`${getSeverityColor(2)} ${currentPercent}%`);
                            currentPercent += altoPercent;
                            gradientStops.push(`${getSeverityColor(2)} ${currentPercent}%`);
                          }

                          if (severityCounts.medio > 0) {
                            gradientStops.push(`${getSeverityColor(1)} ${currentPercent}%`);
                            currentPercent += medioPercent;
                            gradientStops.push(`${getSeverityColor(1)} ${currentPercent}%`);
                          }

                          if (severityCounts.baixo > 0) {
                            gradientStops.push(`${getSeverityColor(0)} ${currentPercent}%`);
                            currentPercent += baixoPercent;
                            gradientStops.push(`${getSeverityColor(0)} ${currentPercent}%`);
                          }

                          return (
                            <div
                              className={styles.severityFill}
                              style={{
                                width: '100%',
                                background: `linear-gradient(to right, ${gradientStops.join(', ')})`,
                                borderRadius: tokens.borderRadiusSmall
                              }}
                            />
                          );
                        })()
                      ) : (
                        <div
                          className={styles.severityFill}
                          style={{
                            width: '100%',
                            backgroundColor: tokens.colorNeutralBackground4,
                            borderRadius: tokens.borderRadiusSmall
                          }}
                        />
                      )}
                    </div>
                    {naoPontuadas.length > 0 && (
                      <div className={styles.severityDetails}>
                        {severityCounts.critico > 0 && (
                          <Text className={styles.severityItem}>
                            Críticas: {severityCounts.critico}
                          </Text>
                        )}
                        {severityCounts.alto > 0 && (
                          <Text className={styles.severityItem}>
                            Altas: {severityCounts.alto}
                          </Text>
                        )}
                        {severityCounts.medio > 0 && (
                          <Text className={styles.severityItem}>
                            Médias: {severityCounts.medio}
                          </Text>
                        )}
                        {severityCounts.baixo > 0 && (
                          <Text className={styles.severityItem}>
                            Baixas: {severityCounts.baixo}
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <Button
                  appearance="outline"
                  size="small"
                  icon={<ArrowRight24Regular />}
                  className={styles.actionButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEventClick(evento);
                  }}
                >
                  Ver Detalhes
                </Button>
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
};

export default EventCards;